import React, { useEffect, useReducer, useState } from "react";
import { Collapse, Modal, Button, Alert } from "react-bootstrap";
import { Link } from "react-router-dom";

const initialState = {
  openCollapse1: true,
  openCollapse2: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "openCollapse1":
      return { ...state, openCollapse1: !state.openCollapse1 };
    case "openCollapse2":
      return { ...state, openCollapse2: !state.openCollapse2 };
    default:
      return state;
  }
};

const emptyForm = {
  title: "",
  slug: "",
  short_description: "",
  badge_title: "",
  badge_subtitle: "",
  main_heading: "",
  para1: "",
  para2: "",
  quote_text: "",
  feature1: "",
  feature2: "",
  feature3: "",
  feature4: "",
  procedure1_title: "",
  procedure1_desc: "",
  procedure2_title: "",
  procedure2_desc: "",
  procedure3_title: "",
  procedure3_desc: "",
  procedure4_title: "",
  procedure4_desc: "",
};

const Treatments = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [treatments, setTreatments] = useState([]);
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);

  const [alert, setAlert] = useState({
    show: false,
    type: "",
    text: "",
  });

  const [form, setForm] = useState(emptyForm);

  // DELETE MODAL
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // EDIT MODAL
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editImage, setEditImage] = useState(null);
  const [editData, setEditData] = useState({ ...emptyForm, image: "" });

  // ALERT
  const showAlert = (type, text) => {
    setAlert({ show: true, type, text });
    setTimeout(() => {
      setAlert({ show: false, type: "", text: "" });
    }, 3000);
  };

  // FETCH TREATMENTS
  const fetchTreatments = async () => {
    try {
      const res = await fetch("https://admin.vascularirdoctors.com/backend/treatments");
      const data = await res.json();
      setTreatments(data);
    } catch (err) {
      console.log("FETCH ERROR:", err);
      showAlert("danger", "Unable to fetch treatments.");
    }
  };

  useEffect(() => {
    fetchTreatments();
  }, []);

  // FORM CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "title") {
      setForm({
        ...form,
        title: value,
        slug: value
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .replace(/\s+/g, "-"),
      });
    } else {
      setForm({
        ...form,
        [name]: value,
      });
    }
  };

  const handleImage = (e) => {
    setImage(e.target.files[0]);
  };

  // ADD TREATMENT
  const submitHandler = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();

      Object.keys(form).forEach((key) => {
        data.append(key, form[key]);
      });

      if (image) data.append("image", image);

      const res = await fetch("https://admin.vascularirdoctors.com/backend/treatments", {
        method: "POST",
        body: data,
      });

      const result = await res.json();

      if (res.ok) {
        showAlert("success", "Treatment added successfully.");
        setForm(emptyForm);
        setImage(null);
        fetchTreatments();
      } else {
        showAlert("danger", result.message || "Failed to save treatment.");
      }
    } catch (err) {
      console.log("ADD ERROR:", err);
      showAlert("danger", "Server error while saving treatment.");
    }

    setLoading(false);
  };

  // DELETE
  const confirmDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const deleteItem = async () => {
    try {
      const res = await fetch(`https://admin.vascularirdoctors.com/backend/treatments/${deleteId}`, {
        method: "DELETE",
      });

      const result = await res.json();

      if (res.ok) {
        showAlert("success", "Treatment deleted successfully.");
        fetchTreatments();
      } else {
        showAlert("danger", result.message || "Delete failed.");
      }
    } catch (err) {
      console.log("DELETE ERROR:", err);
      showAlert("danger", "Unable to delete treatment.");
    }

    setShowDeleteModal(false);
    setDeleteId(null);
  };

  // OPEN EDIT
  const openEdit = (item) => {
    setEditId(item.id);
    setEditData({
      title: item.title || "",
      slug: item.slug || "",
      short_description: item.short_description || "",
      badge_title: item.badge_title || "",
      badge_subtitle: item.badge_subtitle || "",
      main_heading: item.main_heading || "",
      para1: item.para1 || "",
      para2: item.para2 || "",
      quote_text: item.quote_text || "",
      feature1: item.feature1 || "",
      feature2: item.feature2 || "",
      feature3: item.feature3 || "",
      feature4: item.feature4 || "",
      procedure1_title: item.procedure1_title || "",
      procedure1_desc: item.procedure1_desc || "",
      procedure2_title: item.procedure2_title || "",
      procedure2_desc: item.procedure2_desc || "",
      procedure3_title: item.procedure3_title || "",
      procedure3_desc: item.procedure3_desc || "",
      procedure4_title: item.procedure4_title || "",
      procedure4_desc: item.procedure4_desc || "",
      image: item.image || "",
    });
    setEditImage(null);
    setShowModal(true);
  };

  // EDIT CHANGE
  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "title") {
      setEditData({
        ...editData,
        title: value,
        slug: value
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "")
          .replace(/\s+/g, "-"),
      });
    } else {
      setEditData({
        ...editData,
        [name]: value,
      });
    }
  };

  const handleEditImage = (e) => {
    setEditImage(e.target.files[0]);
  };

  // UPDATE
  const updateItem = async () => {
    try {
      const data = new FormData();

      Object.keys(editData).forEach((key) => {
        if (key !== "image") {
          data.append(key, editData[key]);
        }
      });

      if (editImage) data.append("image", editImage);

      const res = await fetch(`https://admin.vascularirdoctors.com/backend/treatments/${editId}`, {
        method: "PUT",
        body: data,
      });

      const result = await res.json();

      if (res.ok) {
        showAlert("success", "Treatment updated successfully.");
        setShowModal(false);
        fetchTreatments();
      } else {
        showAlert("danger", result.message || "Update failed.");
      }
    } catch (err) {
      console.log("UPDATE ERROR:", err);
      showAlert("danger", "Unable to update treatment.");
    }
  };

  return (
    <>
      <div className="row">
        <div className="col-xl-12">
          {/* PAGE TITLE */}
          <div className="row page-titles align-items-center">
            <div className="col-sm-6">
              <ol className="breadcrumb mt-sm-0 mt-2">
                
                <li className="breadcrumb-item active" aria-current="page">
                  Treatments
                </li>
              </ol>
            </div>

            {/* CLOSE BUTTON */}
            <div className="col-sm-6 text-sm-end mt-2 mt-sm-0">
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={() => dispatch({ type: "openCollapse1" })}
              >
                <i className="fas fa-times me-1"></i> Close
              </button>
            </div>
          </div>
        </div>

        {/* ALERT */}
        {alert.show && (
          <div className="col-xl-12">
            <Alert variant={alert.type} className="shadow-sm rounded-3">
              {alert.text}
            </Alert>
          </div>
        )}

        {/* ADD FORM */}
        <div className="col-xl-12">
          <div className="filter cm-content-box box-primary">
            <div className="content-title d-flex justify-content-between align-items-center">
              <div className="cpa">
                <i className="fas fa-notes-medical me-1"></i>Add Treatment
              </div>
              <div className="tools">
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  onClick={() => dispatch({ type: "openCollapse1" })}
                >
                  <i
                    className={`fas ${
                      state.openCollapse1 ? "fa-angle-up" : "fa-angle-down"
                    }`}
                  ></i>
                </button>
              </div>
            </div>

            <Collapse in={state.openCollapse1}>
              <div className="cm-content-body form excerpt">
                <div className="card-body">
                  <form onSubmit={submitHandler}>
                    <div className="row">
                      {/* BASIC INFO */}
                      <div className="col-xl-12 mb-3">
                        <h5 className="mb-3 border-bottom pb-2">
                          Basic Information
                        </h5>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Treatment Title</label>
                        <input
                          className="form-control"
                          name="title"
                          value={form.title}
                          onChange={handleChange}
                          placeholder="Enter treatment title"
                          required
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Slug</label>
                        <input
                          className="form-control"
                          name="slug"
                          value={form.slug}
                          onChange={handleChange}
                          placeholder="Auto-generated slug"
                          required
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Short Description</label>
                        <textarea
                          className="form-control"
                          rows="4"
                          name="short_description"
                          value={form.short_description}
                          onChange={handleChange}
                          placeholder="Short description for homepage card"
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Upload Image</label>
                        <input
                          type="file"
                          className="form-control"
                          onChange={handleImage}
                          accept="image/*"
                        />

                        {image && (
                          <div className="mt-3">
                            <img
                              src={URL.createObjectURL(image)}
                              alt="Preview"
                              style={{
                                width: "100%",
                                maxHeight: "220px",
                                objectFit: "cover",
                                borderRadius: "12px",
                                border: "1px solid #ddd",
                              }}
                            />
                          </div>
                        )}
                      </div>

                      {/* BADGE + MAIN CONTENT */}
                      <div className="col-xl-12 mt-3 mb-3">
                        <h5 className="mb-3 border-bottom pb-2">
                          Inner Page Content
                        </h5>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Badge Title</label>
                        <input
                          className="form-control"
                          name="badge_title"
                          value={form.badge_title}
                          onChange={handleChange}
                          placeholder="Example: Advanced Cardiac Care"
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Badge Subtitle</label>
                        <input
                          className="form-control"
                          name="badge_subtitle"
                          value={form.badge_subtitle}
                          onChange={handleChange}
                          placeholder="Example: Minimally Invasive Procedures"
                        />
                      </div>

                      <div className="col-md-12 mb-3">
                        <label className="form-label">Main Heading</label>
                        <input
                          className="form-control"
                          name="main_heading"
                          value={form.main_heading}
                          onChange={handleChange}
                          placeholder="Main heading for inner page"
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Paragraph 1</label>
                        <textarea
                          className="form-control"
                          rows="5"
                          name="para1"
                          value={form.para1}
                          onChange={handleChange}
                          placeholder="First paragraph"
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">Paragraph 2</label>
                        <textarea
                          className="form-control"
                          rows="5"
                          name="para2"
                          value={form.para2}
                          onChange={handleChange}
                          placeholder="Second paragraph"
                        />
                      </div>

                      <div className="col-md-12 mb-3">
                        <label className="form-label">Quote Text</label>
                        <textarea
                          className="form-control"
                          rows="3"
                          name="quote_text"
                          value={form.quote_text}
                          onChange={handleChange}
                          placeholder="Quote box content"
                        />
                      </div>

                      {/* FEATURES */}
                      <div className="col-xl-12 mt-3 mb-3">
                        <h5 className="mb-3 border-bottom pb-2">Features</h5>
                      </div>

                      {[1, 2, 3, 4].map((i) => (
                        <div className="col-md-6 mb-3" key={i}>
                          <label className="form-label">{`Feature ${i}`}</label>
                          <input
                            className="form-control"
                            name={`feature${i}`}
                            value={form[`feature${i}`]}
                            onChange={handleChange}
                            placeholder={`Enter feature ${i}`}
                          />
                        </div>
                      ))}

                      {/* PROCEDURES */}
                      <div className="col-xl-12 mt-3 mb-3">
                        <h5 className="mb-3 border-bottom pb-2">
                          Procedure Cards
                        </h5>
                      </div>

                      {[1, 2, 3, 4].map((i) => (
                        <React.Fragment key={i}>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">{`Procedure ${i} Title`}</label>
                            <input
                              className="form-control"
                              name={`procedure${i}_title`}
                              value={form[`procedure${i}_title`]}
                              onChange={handleChange}
                              placeholder={`Procedure ${i} title`}
                            />
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">{`Procedure ${i} Description`}</label>
                            <textarea
                              className="form-control"
                              rows="3"
                              name={`procedure${i}_desc`}
                              value={form[`procedure${i}_desc`]}
                              onChange={handleChange}
                              placeholder={`Procedure ${i} description`}
                            />
                          </div>
                        </React.Fragment>
                      ))}

                      {/* SUBMIT */}
                      <div className="col-xl-12 text-end mt-3">
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? "Saving..." : "Save Treatment"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </Collapse>
          </div>
        </div>

        {/* TREATMENT LIST */}
        <div className="col-xl-12 mt-4">
          <div className="filter cm-content-box box-primary">
            <div className="content-title d-flex justify-content-between align-items-center">
              <div className="cpa">
                <i className="fas fa-list me-1"></i>Uploaded Treatments
              </div>
              <div className="tools">
                <button
                  type="button"
                  className="btn btn-sm btn-light border"
                  onClick={() => dispatch({ type: "openCollapse2" })}
                >
                  <i
                    className={`fas ${
                      state.openCollapse2 ? "fa-angle-up" : "fa-angle-down"
                    }`}
                  ></i>
                </button>
              </div>
            </div>

            <Collapse in={state.openCollapse2}>
              <div className="cm-content-body">
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-bordered table-striped align-middle">
                      <thead>
                        <tr>
                          <th>S/N</th>
                          <th>Image</th>
                          <th>Title</th>
                          <th>Slug</th>
                          <th style={{ width: "170px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {treatments.length > 0 ? (
                          treatments.map((item, i) => (
                            <tr key={item.id}>
                              <td>{i + 1}</td>
                              <td>
                                <img
                                  src={
                                    item.image
                                      ? `https://admin.vascularirdoctors.com/backend${item.image}`
                                      : "https://via.placeholder.com/90x60?text=No+Image"
                                  }
                                  alt={item.title}
                                  style={{
                                    width: "90px",
                                    height: "60px",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                  }}
                                />
                              </td>
                              <td>{item.title}</td>
                              <td>{item.slug}</td>
                              <td>
                                <button
                                  className="btn btn-sm btn-info me-2"
                                  onClick={() => openEdit(item)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => confirmDelete(item.id)}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="text-center">
                              No treatments found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </Collapse>
          </div>
        </div>
      </div>

      {/* EDIT MODAL */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Treatment</Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label>Title</label>
              <input
                className="form-control"
                name="title"
                value={editData.title}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Slug</label>
              <input
                className="form-control"
                name="slug"
                value={editData.slug}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Short Description</label>
              <textarea
                className="form-control"
                rows="3"
                name="short_description"
                value={editData.short_description}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Badge Title</label>
              <input
                className="form-control"
                name="badge_title"
                value={editData.badge_title}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Badge Subtitle</label>
              <input
                className="form-control"
                name="badge_subtitle"
                value={editData.badge_subtitle}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Main Heading</label>
              <input
                className="form-control"
                name="main_heading"
                value={editData.main_heading}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Paragraph 1</label>
              <textarea
                className="form-control"
                rows="4"
                name="para1"
                value={editData.para1}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Paragraph 2</label>
              <textarea
                className="form-control"
                rows="4"
                name="para2"
                value={editData.para2}
                onChange={handleEditChange}
              />
            </div>

            <div className="col-md-12 mb-3">
              <label>Quote Text</label>
              <textarea
                className="form-control"
                rows="3"
                name="quote_text"
                value={editData.quote_text}
                onChange={handleEditChange}
              />
            </div>

            {[1, 2, 3, 4].map((i) => (
              <React.Fragment key={i}>
                <div className="col-md-6 mb-3">
                  <label>{`Feature ${i}`}</label>
                  <input
                    className="form-control"
                    name={`feature${i}`}
                    value={editData[`feature${i}`]}
                    onChange={handleEditChange}
                  />
                </div>
              </React.Fragment>
            ))}

            {[1, 2, 3, 4].map((i) => (
              <React.Fragment key={`p-${i}`}>
                <div className="col-md-6 mb-3">
                  <label>{`Procedure ${i} Title`}</label>
                  <input
                    className="form-control"
                    name={`procedure${i}_title`}
                    value={editData[`procedure${i}_title`]}
                    onChange={handleEditChange}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label>{`Procedure ${i} Description`}</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    name={`procedure${i}_desc`}
                    value={editData[`procedure${i}_desc`]}
                    onChange={handleEditChange}
                  />
                </div>
              </React.Fragment>
            ))}

            <div className="col-md-12 mb-3">
              <label>Change Image</label>
              <input
                type="file"
                className="form-control"
                onChange={handleEditImage}
                accept="image/*"
              />
            </div>

            <div className="col-md-12">
              <img
                src={
                  editImage
                    ? URL.createObjectURL(editImage)
                    : editData.image
                    ? `https://admin.vascularirdoctors.com/backend${editData.image}`
                    : "https://via.placeholder.com/600x250?text=No+Image"
                }
                alt="Preview"
                style={{
                  width: "100%",
                  maxHeight: "250px",
                  objectFit: "cover",
                  borderRadius: "10px",
                }}
              />
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="light" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={updateItem}>
            Update Treatment
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE MODAL */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Treatment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this treatment? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={deleteItem}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Treatments;